/**
 * Handles curated kit add-to-cart from PDP and quick-buy modal.
 *
 * Adds the 3 real component variants (2 shirts + pant) so Shopify decrements
 * garment inventory. Groups them with _bundle_id / _is_kit_component for cart display.
 * Parent kit variants supply the kit name + intended kit price (_kit_price) only.
 *
 * Usage: wrap a .product-kit__form with
 *   <curated-kit-form data-kit-name="..." data-parent-handle="...">
 * Include <script type="application/json" data-kit-variants> for parent price matching.
 */
class CuratedKitForm extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.form = this.querySelector('form.product-kit__form');
    this.addButton = this.querySelector('[data-kit-add-button]');
    this.errorDiv = this.querySelector('[data-kit-error]');
    this.variantsEl = this.querySelector('[data-kit-variants]');

    if (!this.form) return;

    this.resetSizeSelects();
    this.updateButtonLabel();
    this.form.addEventListener('change', this.updateButtonLabel.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  normalizeKitOption(str) {
    return String(str || '')
      .replace(/\u00d7/g, 'x')
      .replace(/\s+/g, '')
      .toLowerCase();
  }

  resetSizeSelects() {
    if (!this.form) return;
    this.form.querySelectorAll('.product-kit__size-select').forEach((select) => {
      select.value = '';
      if (select.options.length) select.selectedIndex = 0;
    });
  }

  kitSizesSelected() {
    if (!this.form) return false;
    const selects = this.form.querySelectorAll('.product-kit__size-select');
    if (!selects.length) return true;
    return Array.from(selects).every((select) => !!select.value);
  }

  updateButtonLabel() {
    if (!this.addButton) return;
    const buttonText = this.addButton.querySelector('.button-text');
    const label = this.kitSizesSelected() ? 'Add to cart' : 'Select a size';
    if (buttonText) {
      buttonText.textContent = label;
    } else if (!this.addButton.querySelector('.spinner')) {
      this.addButton.textContent = label;
    }
  }

  setLoading(isLoading) {
    if (!this.addButton) return;
    const buttonText = this.addButton.querySelector('.button-text');
    this.addButton.disabled = isLoading;
    if (isLoading) {
      if (buttonText) {
        buttonText.innerHTML = '<span class="spinner"></span>Adding to Cart...';
      } else {
        this.addButton.innerHTML = '<span class="spinner"></span>Adding to Cart...';
      }
    } else {
      this.updateButtonLabel();
    }
  }

  showError(message) {
    if (!this.errorDiv) return;
    this.errorDiv.textContent = message;
    this.errorDiv.classList.add('show');
  }

  clearError() {
    if (!this.errorDiv) return;
    this.errorDiv.classList.remove('show');
    this.errorDiv.textContent = '';
  }

  optionsMatch(variantOptions, selectedOptions) {
    if (!variantOptions || variantOptions.length !== selectedOptions.length) return false;
    for (let i = 0; i < selectedOptions.length; i++) {
      if (this.normalizeKitOption(variantOptions[i]) !== this.normalizeKitOption(selectedOptions[i])) {
        return false;
      }
    }
    return true;
  }

  async onSubmit(e) {
    e.preventDefault();
    this.clearError();

    const selects = this.form.querySelectorAll('.product-kit__size-select');
    const pieceRows = this.form.querySelectorAll('.product-kit__item');

    let allSelected = true;
    selects.forEach((select) => {
      if (!select.value) allSelected = false;
    });

    if (!allSelected) {
      this.showError('Please select sizes for all items.');
      return;
    }

    let kitVariants = [];
    try {
      kitVariants = this.variantsEl ? JSON.parse(this.variantsEl.textContent) : [];
    } catch (err) {
      kitVariants = [];
    }

    const optionLen = kitVariants.length && kitVariants[0].options ? kitVariants[0].options.length : 0;
    const selectedKitOptions = optionLen > 0 ? new Array(optionLen).fill('') : [];

    const bundleId = Date.now().toString();
    const kitName = this.getAttribute('data-kit-name') || '';
    const parentProductHandle = this.getAttribute('data-parent-handle') || '';

    const pieces = [];
    let pieceIndex = 0;

    pieceRows.forEach((row) => {
      pieceIndex += 1;
      const optIdx = parseInt(row.getAttribute('data-option-index'), 10);
      const select = row.querySelector('.product-kit__size-select');
      const hidden = row.querySelector('input[type="hidden"][name*="_variant"]');
      const title = (row.getAttribute('data-piece-title') || 'Item ' + pieceIndex).trim();
      const color = (row.getAttribute('data-piece-color') || '').trim();
      let variantId = '';
      let sizeLabel = '';
      let parentOptionVal = '';

      if (select && select.value) {
        variantId = select.value;
        const opt = select.options[select.selectedIndex];
        sizeLabel = opt ? opt.text.replace(/\s*\(Sold Out\)\s*$/i, '').trim() : '';
        parentOptionVal = opt ? (opt.getAttribute('data-kit-option-value') || '').trim() : '';
        if (!parentOptionVal) parentOptionVal = sizeLabel;
      } else if (hidden && hidden.value) {
        variantId = hidden.value;
        sizeLabel = 'One Size';
        parentOptionVal = (hidden.getAttribute('data-kit-option-value') || '').trim();
      }

      if (optionLen > 0 && !Number.isNaN(optIdx) && optIdx >= 0 && optIdx < selectedKitOptions.length) {
        selectedKitOptions[optIdx] = parentOptionVal;
      }

      let lineLabel = title;
      if (color && sizeLabel) {
        lineLabel = title + ': ' + color + ' - ' + sizeLabel;
      } else if (color) {
        lineLabel = title + ': ' + color;
      } else if (sizeLabel) {
        lineLabel = title + ': ' + sizeLabel;
      }

      if (variantId) {
        pieces.push({
          variantId: String(variantId),
          lineLabel,
          title,
          color,
          sizeLabel,
          index: pieceIndex
        });
      }
    });

    if (pieces.length === 0 || pieces.length !== pieceRows.length) {
      this.showError('Please select sizes for all items.');
      return;
    }

    // Parent variant is for kit price metadata only — inventory comes from piece variants.
    let matchedKitVariant = null;
    if (optionLen > 0 && selectedKitOptions.every((s) => s)) {
      matchedKitVariant = kitVariants.find(
        (v) => v.available && v.options && this.optionsMatch(v.options, selectedKitOptions)
      );
      if (!matchedKitVariant) {
        matchedKitVariant = kitVariants.find(
          (v) => v.options && this.optionsMatch(v.options, selectedKitOptions)
        );
      }
    }

    const kitPriceCents =
      matchedKitVariant && matchedKitVariant.price != null ? String(matchedKitVariant.price) : '';

    const sharedProps = {
      _bundle_id: bundleId,
      _kit_name: kitName,
      _parent_handle: parentProductHandle,
      _is_kit_component: 'true',
      _kit_type: 'curated',
      _kit_piece_count: String(pieces.length)
    };
    if (kitPriceCents) {
      sharedProps._kit_price = kitPriceCents;
    }

    let shirtSlot = 0;
    const items = pieces.map((piece) => {
      const titleDown = (piece.title || '').toLowerCase();
      let componentLabel = 'Piece ' + piece.index;
      if (
        titleDown.includes('trouser') ||
        titleDown.includes('pant') ||
        titleDown.includes('driggs') ||
        titleDown.includes('welt')
      ) {
        componentLabel = 'Pant';
      } else if (
        titleDown.includes('shirt') ||
        titleDown.includes('oxford') ||
        titleDown.includes('english') ||
        titleDown.includes('austin')
      ) {
        shirtSlot += 1;
        componentLabel = shirtSlot === 1 ? 'Shirt 1' : 'Shirt 2';
      }

      const variantId = Number(piece.variantId);
      return {
        id: variantId,
        quantity: 1,
        properties: Object.assign({}, sharedProps, {
          _kitComponent: componentLabel,
          _kit_piece: piece.lineLabel,
          _kit_v: String(piece.variantId)
        })
      };
    });

    // Guard: invalid IDs
    if (items.some((item) => !item.id || !Number.isFinite(item.id))) {
      this.showError('Could not resolve kit piece sizes. Please refresh and try again.');
      return;
    }

    this.setLoading(true);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      let responseJson = {};
      try {
        responseJson = await response.json();
      } catch (parseErr) {
        responseJson = {};
      }
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!response.ok) {
        const msg =
          responseJson.description ||
          responseJson.message ||
          (response.status === 422
            ? 'One or more kit pieces are unavailable. Please choose different sizes.'
            : 'Failed to add items to cart');
        throw new Error(msg);
      }

      const usePageCart =
        (window.themeVariables &&
          window.themeVariables.settings &&
          (window.themeVariables.settings.cartType === 'page' ||
            window.themeVariables.settings.pageType === 'cart')) ||
        this.getAttribute('data-redirect-cart') === 'true';

      if (usePageCart) {
        window.location.href = `${root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      this.form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items ? responseJson.items : [responseJson],
            cart: cartContent,
            onSuccessDo: 'force_open_drawer'
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: 'force_open_drawer',
            cart: cartContent
          }
        })
      );

      this.setLoading(false);
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this.showError(error.message || 'Error adding items to cart. Please try again.');
      this.setLoading(false);
    }
  }
}

if (!window.customElements.get('curated-kit-form')) {
  window.customElements.define('curated-kit-form', CuratedKitForm);
}

/**
 * Make quick-buy gallery arrows loop (last → first, first → last).
 * Prestige scroll-carousel clamps at edges and disables arrow buttons; we override that
 * inside kit and individual-item quick-buy modals.
 */
function enableQuickBuyGalleryLoop(root) {
  const carousel = root.querySelector('scroll-carousel.product-gallery__carousel, scroll-carousel');
  if (!carousel || carousel.dataset.qbLoop === 'true') return;
  carousel.dataset.qbLoop = 'true';

  const prev = root.querySelector('carousel-prev-button');
  const next = root.querySelector('carousel-next-button');

  const forceEnabled = () => {
    if (prev && prev.firstElementChild) prev.firstElementChild.disabled = false;
    if (next && next.firstElementChild) next.firstElementChild.disabled = false;
  };

  carousel.addEventListener('scroll:edge-nearing', forceEnabled);
  carousel.addEventListener('scroll:edge-leaving', forceEnabled);
  carousel.addEventListener('carousel:change', forceEnabled);
  forceEnabled();

  if (prev) {
    prev.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const len = carousel.cells ? carousel.cells.length : 0;
        if (len < 2) return;
        const index = typeof carousel.selectedIndex === 'number' ? carousel.selectedIndex : 0;
        carousel.select((index - 1 + len) % len);
        forceEnabled();
      },
      true
    );
  }

  if (next) {
    next.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const len = carousel.cells ? carousel.cells.length : 0;
        if (len < 2) return;
        const index = typeof carousel.selectedIndex === 'number' ? carousel.selectedIndex : 0;
        carousel.select((index + 1) % len);
        forceEnabled();
      },
      true
    );
  }
}

function scanQuickBuyGalleries() {
  document
    .querySelectorAll('.product-kit-quick-buy, .product-item-quick-buy')
    .forEach(enableQuickBuyGalleryLoop);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scanQuickBuyGalleries);
} else {
  scanQuickBuyGalleries();
}

const quickBuyGalleryLoopObserver = new MutationObserver(() => scanQuickBuyGalleries());
quickBuyGalleryLoopObserver.observe(document.documentElement, { childList: true, subtree: true });
